import { useCallback, useEffect, useRef, useState } from 'react';
import type { FreigabePublicErgebnis, GbuUnterschrift } from '../../../../services/sifa/gbuTypes';
import { ladeFreigabe, unterschreibeFreigabe } from '../../../../services/sifa/gbuPublicService';
import { erzeugeGbuPdf } from '../../../../services/sifa/gbuPdf';
import { describeError } from '../../../../services/errorMessage';
import {
  bereinigeName,
  bisherigeUnterschriften,
  ergebnisNachUnterschrift,
  kannFreigeben,
  mussNachUnterschriftNachladen,
  nachgeladeneDaten,
  zustandNachLaden,
  type FreigabeZustand,
} from './gbuFreigabeSeiteLogic';

export type FreigabeDaten = Extract<FreigabePublicErgebnis, { ok: true }>;

export type PdfStand =
  | { status: 'keins' }
  | { status: 'erzeugt' }
  | { status: 'bereit'; url: string }
  | { status: 'fehler'; text: string };

/**
 * Zustand der oeffentlichen Freigabeseite #/gbu-freigabe/<token>.
 *
 * Laden ueber `ladeFreigabe` (anon-RPC sifa_gbu_freigabe_public), Freigeben
 * ueber `unterschreibeFreigabe` (sifa_gbu_freigabe_unterschreiben). Beide
 * liefern Fehler als Rueckgabe; ein Wurf gilt trotzdem als Netzfehler, damit
 * die Seite nie haengen bleibt.
 *
 * Das PDF entsteht erst auf Knopfdruck im Browser (jsPDF wird in gbuPdf.ts
 * nachgeladen) aus dem eingefrorenen Inhalt und den bisherigen
 * Unterschriften — dieselbe Quelle wie die Ablage im Buero.
 */
export function useGbuFreigabe(token: string) {
  const [zustand, setZustand] = useState<FreigabeZustand>('laedt');
  const [daten, setDaten] = useState<FreigabeDaten | null>(null);
  const [unterschriften, setUnterschriften] = useState<GbuUnterschrift[]>([]);
  const [name, setName] = useState('');
  const [gelesen, setGelesen] = useState(false);
  const [bild, setBild] = useState<string | null>(null);
  const [padOffen, setPadOffen] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [pdf, setPdf] = useState<PdfStand>({ status: 'keins' });
  /** Nach der Unterschrift wird die Fassung neu geladen; solange ruht der PDF-Knopf. */
  const [nachlaedt, setNachlaedt] = useState(false);

  // Riegel gegen den zweiten Tipp auf „Freigeben": `sendet` steht erst nach
  // dem naechsten Render, der Link gilt nur einmal.
  const sendetRef = useRef(false);
  const ladeLauf = useRef(0);
  const pdfUrlRef = useRef<string | null>(null);
  // Zaehlt jedes Verwerfen: ein PDF, das waehrenddessen noch entsteht, gehoert
  // zum alten Stand und wird nicht mehr angezeigt.
  const pdfLauf = useRef(0);

  const laden = useCallback(async () => {
    const lauf = ++ladeLauf.current;
    setZustand('laedt');
    let e: FreigabePublicErgebnis;
    try {
      e = await ladeFreigabe(token);
    } catch {
      e = { ok: false, fehler: 'netz' };
    }
    if (lauf !== ladeLauf.current) return;
    if (e.ok) {
      setDaten(e);
      setUnterschriften(bisherigeUnterschriften(e));
      // Vorgabe aus dem Link nur, solange niemand selbst etwas getippt hat.
      setName((bisher) => bisher || e.nameVorgabe || '');
    }
    setZustand(zustandNachLaden(e));
  }, [token]);

  useEffect(() => {
    void laden();
  }, [laden]);

  // Blob-Adresse beim Verlassen freigeben.
  useEffect(() => () => {
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
  }, []);

  /** Erzeugtes PDF verwerfen — das naechste entsteht aus dem neuen Stand. */
  const pdfVerwerfen = useCallback(() => {
    pdfLauf.current += 1;
    if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    pdfUrlRef.current = null;
    setPdf({ status: 'keins' });
  }, []);

  /**
   * Nach der Unterschrift: Fassung neu laden, damit das PDF die neue
   * Unterschrift zeigt. Uebernommen werden nur die Daten, der Zustand
   * (Freigegeben) bleibt (nachgeladeneDaten).
   */
  const datenNachladen = useCallback(async () => {
    const lauf = ++ladeLauf.current;
    pdfVerwerfen();
    setNachlaedt(true);
    let e: FreigabePublicErgebnis;
    try {
      e = await ladeFreigabe(token);
    } catch {
      e = { ok: false, fehler: 'netz' };
    }
    if (lauf !== ladeLauf.current) return;
    const neu = nachgeladeneDaten(e);
    if (neu) {
      setDaten(neu);
      setUnterschriften(bisherigeUnterschriften(neu));
    }
    setNachlaedt(false);
  }, [pdfVerwerfen, token]);

  /**
   * PDF erzeugen und oeffnen. Nach dem `await` darf der Browser das neue
   * Fenster blockieren (Handy); die Seite zeigt deshalb danach immer die
   * Links „PDF öffnen" und „Herunterladen".
   */
  const pdfAnsehen = useCallback(async () => {
    if (!daten || nachlaedt || pdf.status === 'erzeugt') return;
    if (pdf.status === 'bereit') {
      window.open(pdf.url, '_blank', 'noopener');
      return;
    }
    const lauf = pdfLauf.current;
    setPdf({ status: 'erzeugt' });
    try {
      const blob = await erzeugeGbuPdf(daten.inhalt, { unterschriften, leereUnterschriftsfelder: false, entwurf: false });
      if (lauf !== pdfLauf.current) return;
      const url = URL.createObjectURL(blob);
      pdfUrlRef.current = url;
      setPdf({ status: 'bereit', url });
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      if (lauf !== pdfLauf.current) return;
      setPdf({ status: 'fehler', text: `Das PDF ließ sich nicht erzeugen: ${describeError(err)}` });
    }
  }, [daten, nachlaedt, pdf, unterschriften]);

  const unterschriftUebernehmen = useCallback((dataUrl: string) => {
    setBild(dataUrl);
    setPadOffen(false);
    setFehler(null);
  }, []);

  const unterschriftLoeschen = useCallback(() => {
    setBild(null);
    setFehler(null);
  }, []);

  const freigeben = useCallback(async () => {
    if (sendetRef.current) return;
    if (!kannFreigeben({ name, gelesen, bild }) || !bild) return;
    sendetRef.current = true;
    setSendet(true);
    setFehler(null);
    try {
      const e = await unterschreibeFreigabe(token, bereinigeName(name), bild);
      const folge = ergebnisNachUnterschrift(e);
      setZustand(folge.zustand);
      setFehler(folge.fehler);
      if (mussNachUnterschriftNachladen(folge.zustand)) void datenNachladen();
    } catch {
      const folge = ergebnisNachUnterschrift({ ok: false, fehler: 'netz' });
      setZustand(folge.zustand);
      setFehler(folge.fehler);
    } finally {
      sendetRef.current = false;
      setSendet(false);
    }
  }, [bild, datenNachladen, gelesen, name, token]);

  return {
    zustand,
    daten,
    unterschriften,
    name,
    setName,
    gelesen,
    setGelesen,
    bild,
    padOffen,
    padOeffnen: () => setPadOffen(true),
    padSchliessen: () => setPadOffen(false),
    unterschriftUebernehmen,
    unterschriftLoeschen,
    sendet,
    fehler,
    freigeben,
    pdf,
    pdfAnsehen,
    nachlaedt,
    neuLaden: laden,
  };
}
